import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CardPool,
  CardPoolKind,
  CustomerUser,
  EmspStation,
  TokenType,
} from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { createTokenApi } from '../../../redux/slices/RfidTokenSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import MultiSelectDropdown, { Option } from '../../../shared-components/multi-select-field/MultiSelectField';
import SelectField from '../../../shared-components/select-field/SelectField';
import rfidApi from '../../../utils/api/rfid';

interface AddCardModalProperties {
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Extracts a numeric balance value from either decimal object or number
 * @param {any} balanceKwh - Raw balance value
 * @returns {number} Formatted number
 */
const getBalanceKwh = (balanceKwh: any): number => {
  if (typeof balanceKwh === 'object' && balanceKwh !== null && '$numberDecimal' in balanceKwh) {
    return Number.parseFloat(balanceKwh.$numberDecimal) || 0;
  }
  if (typeof balanceKwh === 'number') {
    return balanceKwh;
  }
  if (typeof balanceKwh === 'string') {
    return Number.parseFloat(balanceKwh) || 0;
  }
  return 0;
};

/**
 * Resolves the full tenant network station ID (e.g. IN:ENR:1bfd0119-...)
 * @param {EmspStation} station - Station object
 * @returns {string} Fully qualified station ID
 */
const getFullStationId = (station: EmspStation): string => {
  const baseId = station.stationId || station.id || station._id || '';
  if (!baseId) return '';
  if (baseId.includes(':')) return baseId;
  const prefix = [station.countryCode, station.partyId].filter(Boolean).join(':');
  return prefix ? `${prefix}:${baseId}` : baseId;
};

/**
 * Modal form component to register a new RFID card / token
 * @param {AddCardModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Add card form modal content
 */
function AddCardModal(properties: AddCardModalProperties): React.JSX.Element {
  const { onClose, onSuccess } = properties;
  const { t, i18n } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const { actionLoading } = useAppSelector((state) => state.rfidTokens);

  const [uid, setUid] = useState('');
  const [cardType, setCardType] = useState<TokenType>('BUSINESS_TAP');
  const [businessType, setBusinessType] = useState<CardPoolKind>('GROUP');
  const [customerId, setCustomerId] = useState('');
  const [customerList, setCustomerList] = useState<CustomerUser[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [groupList, setGroupList] = useState<CardPool[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [stationList, setStationList] = useState<EmspStation[]>([]);
  const [stationOptions, setStationOptions] = useState<Option[]>([]);
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: string; labelNode?: React.ReactNode }[]
  >([]);
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string; labelNode?: React.ReactNode }[]
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [errors, setErrors] = useState<{
    uid?: string;
    selectedStationIds?: string;
    selectedGroupId?: string;
    customerId?: string;
  }>({});

  useEffect(() => {
    let isMounted = true;

    if (cardType === 'BUSINESS_TAP' && businessType === 'INDIVIDUAL') {
      setStationsLoading(true);

      /**
       * Loads available charging stations for assignment
       * @returns {Promise<void>} Resolves when stations are loaded
       */
      const loadStationOptions = async (): Promise<void> => {
        try {
          const stationResponse = await rfidApi.fetchStationsList();

          if (isMounted) {
            const rawResults =
              stationResponse?.data?.results ||
              stationResponse?.data?.data ||
              (Array.isArray(stationResponse?.data) ? stationResponse?.data : []);
            const list: EmspStation[] = Array.isArray(rawResults) ? rawResults : [];
            setStationList(list);

            const stnOptions: Option[] = list.map((item: EmspStation) => {
              const fullId = getFullStationId(item);
              const party = [item.countryCode, item.partyId].filter(Boolean).join(':');
              const displayName = item.name || fullId;
              const labelText = item.name
                ? `${item.name}${party ? ` (${party})` : ''} - ${fullId}`
                : `${fullId}`;

              return {
                ...item,
                label: labelText,
                value: fullId,
                labelNode: (
                  <div className="rfid-customer-select-item">
                    <span className="rfid-customer-select-item__name">{displayName}</span>
                    <span className="rfid-customer-select-item__id">{fullId}</span>
                    {party && <span className="rfid-customer-select-item__contact">{party}</span>}
                  </div>
                ),
              };
            });

            setStationOptions(stnOptions);
            setSelectedStationIds((previous) =>
              previous.filter((id) => stnOptions.some((opt) => opt.value === id)),
            );
          }
        } catch (error) {
          console.error('Failed to load stations:', error);
          if (isMounted) {
            setStationOptions([]);
          }
        } finally {
          if (isMounted) {
            setStationsLoading(false);
          }
        }
      };

      loadStationOptions();
    }

    return () => {
      isMounted = false;
    };
  }, [cardType, businessType]);

  useEffect(() => {
    let isMounted = true;

    if (cardType === 'BUSINESS_TAP' && businessType === 'GROUP') {
      setGroupsLoading(true);

      /**
       * Loads available card groups from EMSP API
       * @returns {Promise<void>} Resolves when card groups are loaded
       */
      const loadCardGroups = async (): Promise<void> => {
        try {
          const response = await rfidApi.fetchCardPools({ kind: 'GROUP' });
          if (isMounted) {
            const rawResults =
              response?.data?.results ||
              response?.data?.data ||
              (Array.isArray(response?.data) ? response?.data : []);
            const list: CardPool[] = Array.isArray(rawResults) ? rawResults : [];
            setGroupList(list);

            const options = list.map((group: CardPool) => {
              const groupId = group._id || group.id || '';
              const groupName = group.name || 'Card Group';
              const balance = getBalanceKwh(group.balanceKwh);
              const labelText = `${groupName} (${groupId}) - ${balance} kWh`;

              return {
                label: labelText,
                value: groupId,
                labelNode: (
                  <div className="rfid-customer-select-item">
                    <span className="rfid-customer-select-item__name">{groupName}</span>
                    <span className="rfid-customer-select-item__id">{groupId}</span>
                    <span className="rfid-customer-select-item__contact">{balance} kWh</span>
                  </div>
                ),
              };
            });

            setGroupOptions(options);
            setSelectedGroupId((previous) => {
              const stillValid = options.some((opt) => opt.value === previous);
              return stillValid && previous ? previous : '';
            });
          }
        } catch (error) {
          console.error('Failed to load card groups:', error);
          if (isMounted) {
            setGroupOptions([]);
          }
        } finally {
          if (isMounted) {
            setGroupsLoading(false);
          }
        }
      };

      loadCardGroups();
    }

    return () => {
      isMounted = false;
    };
  }, [cardType, businessType]);

  useEffect(() => {
    let isMounted = true;

    if (cardType === 'PERSONAL_TAP' || (cardType === 'BUSINESS_TAP' && businessType === 'INDIVIDUAL')) {
      setCustomersLoading(true);

      /**
       * Loads customer list from EMSP API
       * @returns {Promise<void>} Resolves when customers are loaded
       */
      const loadCustomers = async (): Promise<void> => {
        try {
          const response = await rfidApi.fetchCustomers();
          if (isMounted) {
            const rawResults =
              response?.data?.results ||
              response?.data?.data ||
              (Array.isArray(response?.data) ? response?.data : []);
            const list: CustomerUser[] = Array.isArray(rawResults) ? rawResults : [];
            setCustomerList(list);

            const custOptions = list.map((item: CustomerUser) => {
              const contactInfo = [item.email, item.phoneNumber].filter(Boolean).join(' | ');
              const labelText = item.name
                ? `${item.name}${contactInfo ? ` (${contactInfo})` : ''} - ${item.userCustomId}`
                : `${item.userCustomId}${contactInfo ? ` (${contactInfo})` : ''}`;

              return {
                label: labelText,
                value: item.userCustomId,
                labelNode: (
                  <div className="rfid-customer-select-item">
                    <span className="rfid-customer-select-item__name">
                      {item.name || item.userCustomId}
                    </span>
                    <span className="rfid-customer-select-item__id">{item.userCustomId}</span>
                    {contactInfo && (
                      <span className="rfid-customer-select-item__contact">{contactInfo}</span>
                    )}
                  </div>
                ),
              };
            });

            setCustomerOptions(custOptions);
            setCustomerId((previous) => {
              const stillValid = custOptions.some((opt) => opt.value === previous);
              return stillValid && previous ? previous : '';
            });
          }
        } catch (error) {
          console.error('Failed to load customers list:', error);
          if (isMounted) {
            setCustomerOptions([]);
          }
        } finally {
          if (isMounted) {
            setCustomersLoading(false);
          }
        }
      };

      loadCustomers();
    }

    return () => {
      isMounted = false;
    };
  }, [cardType, businessType]);

  /**
   * Handles switching of card type and resets dependent fields and errors
   * @param {any} value - Selected card type
   * @returns {void}
   */
  const handleTypeChange = (value: any): void => {
    const nextType = value as TokenType;
    setCardType(nextType);
    setErrors({});
    if (nextType === 'BUSINESS_TAP') {
      setCustomerId('');
    } else {
      setSelectedStationIds([]);
      setSelectedGroupId('');
    }
  };

  /**
   * Handles switching of business assignment type and resets error state
   * @param {CardPoolKind} value - Selected assignment type
   * @returns {void}
   */
  const handleBusinessTypeChange = (value: CardPoolKind): void => {
    setBusinessType(value);
    setErrors((previous) => ({
      ...previous,
      selectedStationIds: undefined,
      selectedGroupId: undefined,
    }));
  };

  /**
   * Handles submission of the add card modal form with validation
   * @returns {Promise<void>} Resolves when token creation completes
   */
  const handleSubmit = async (): Promise<void> => {
    const cleanUid = uid.trim().toUpperCase();
    const newErrors: {
      uid?: string;
      selectedStationIds?: string;
      selectedGroupId?: string;
      customerId?: string;
    } = {};

    if (!cleanUid) {
      newErrors.uid = t('uidRequired', { defaultValue: 'Card number is required' });
    }

    if (cardType === 'BUSINESS_TAP') {
      if (businessType === 'GROUP' && !selectedGroupId) {
        newErrors.selectedGroupId = t('groupRequired', {
          defaultValue: 'Please select a card group',
        });
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrorMessage = Object.values(newErrors)[0];
      if (firstErrorMessage) {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            code: 400,
            message: firstErrorMessage,
          }),
        );
      }
      return;
    }

    let linkedCustomerId: string | null = null;
    let assignedGroupId: string | null = null;
    let assignedStationIds: string[] | undefined;

    if (cardType === 'PERSONAL_TAP') {
      linkedCustomerId = customerId.trim() || null;
    } else if (businessType === 'INDIVIDUAL') {
      assignedGroupId = null;
      assignedStationIds = selectedStationIds.length > 0 ? selectedStationIds : undefined;
      linkedCustomerId = customerId.trim() || null;
    } else if (businessType === 'GROUP') {
      assignedGroupId = selectedGroupId || null;
      assignedStationIds = undefined;
    }

    try {
      await dispatch(
        createTokenApi({
          uid: cleanUid,
          type: cardType,
          customerId: linkedCustomerId,
          cardPoolId: assignedGroupId,
          stationIds: assignedStationIds,
          businessType: cardType === 'BUSINESS_TAP' ? businessType : null,
        }),
      ).unwrap();

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch {
      // Error notification toast is dispatched automatically by createTokenApi
    }
  };

  /**
   * Handles cancelling card creation, resetting form state and closing panel
   * @returns {void}
   */
  const handleCancel = (): void => {
    setUid('');
    setCardType('BUSINESS_TAP');
    setBusinessType('GROUP');
    setSelectedGroupId('');
    setSelectedStationIds([]);
    setCustomerId('');
    setErrors({});
    onClose();
  };

  const typeOptions = [
    {
      label: t('businessCard', { defaultValue: 'Business Card' }),
      value: 'BUSINESS_TAP',
    },
    {
      label: t('personalCard', { defaultValue: 'Personal Card' }),
      value: 'PERSONAL_TAP',
    },
  ];

  const businessTypeOptions = [
    {
      label: t('assignedStations', {
        defaultValue: 'Individual',
      }),
      value: 'INDIVIDUAL',
    },
    {
      label: t('cardGroup', { defaultValue: 'Card Group' }),
      value: 'GROUP',
    },
  ];

  const selectedCustomer = customerList.find((item) => item.userCustomId === customerId);
  const selectedGroup = groupList.find((item) => (item._id || item.id) === selectedGroupId);
  const selectedStations = stationList.filter((item) =>
    selectedStationIds.includes(getFullStationId(item)),
  );

  return (
    <div className="rfid-modal-form" dir={i18n.dir()}>
      <div className="rfid-modal-form__body">
        <div className="rfid-modal-form__field">
          <CustomInput
            inputLabel={t('cardNumberOrUid', { defaultValue: 'Card Number / UID' })}
            type="text"
            name="uid"
            placeholder="e.g. 04A1B2C3D4E5F6"
            value={uid}
            valueRequired
            error={Boolean(errors.uid)}
            errorMessage={errors.uid}
            onChangeValue={(_, value) => {
              setUid(String(value).toUpperCase());
              if (errors.uid) {
                setErrors((previous) => ({ ...previous, uid: undefined }));
              }
            }}
          />
        </div>

        <div className="rfid-modal-form__field">
          <SelectField
            inputLabel={t('cardType', { defaultValue: 'Card Type' })}
            name="cardType"
            options={typeOptions}
            selectedValue={cardType}
            valueRequired
            onChangeValue={(_, value) => handleTypeChange(value)}
          />
        </div>

        {cardType === 'BUSINESS_TAP' && (
          <div className="rfid-modal-form__field">
            <SelectField
              inputLabel={t('assignmentType', { defaultValue: 'Assignment Type' })}
              name="businessType"
              options={businessTypeOptions}
              selectedValue={businessType}
              valueRequired
              onChangeValue={(_, value) => handleBusinessTypeChange(value as CardPoolKind)}
            />
          </div>
        )}

        {cardType === 'BUSINESS_TAP' && businessType === 'INDIVIDUAL' && (
          <div className="rfid-modal-form__field">
            <MultiSelectDropdown
              title={t('chargingStations', { defaultValue: 'Charging Stations' })}
              name="selectedStationIds"
              options={stationOptions}
              selectedValues={selectedStationIds}
              drillDownLevels={['countryCode', 'partyId']}
              error={Boolean(errors.selectedStationIds)}
              errorMessage={errors.selectedStationIds}
              onSelectionChange={(_, values) => {
                setSelectedStationIds(values);
                if (errors.selectedStationIds) {
                  setErrors((previous) => ({ ...previous, selectedStationIds: undefined }));
                }
              }}
              placeholder={
                stationsLoading
                  ? t('loadingStations', { defaultValue: 'Loading charging stations...' })
                  : t('selectStationsPlaceholder', {
                      defaultValue: 'Select one or more charging stations',
                    })
              }
              placeholderFilled={
                selectedStationIds.length > 1
                  ? `${selectedStationIds.length} ${t('stations', { defaultValue: 'stations' })}`
                  : t('station', { defaultValue: 'station' })
              }
              search
            />
            {errors.selectedStationIds && (
              <span className="text-danger">{errors.selectedStationIds}</span>
            )}

            {selectedStations.length > 0 && (
              <div className="rfid-selected-stations-list">
                <div className="rfid-selected-stations-list__header">
                  <span className="rfid-selected-stations-list__title">
                    {t('selectedStationsCount', {
                      defaultValue: 'Selected Stations',
                    })}{' '}
                    ({selectedStations.length})
                  </span>
                  {selectedStations.length > 1 && (
                    <button
                      type="button"
                      className="rfid-selected-stations-list__clear-btn"
                      onClick={() => setSelectedStationIds([])}>
                      {t('clearAll', { defaultValue: 'Clear all' })}
                    </button>
                  )}
                </div>
                <div className="rfid-selected-stations-list__items">
                  {selectedStations.map((station) => {
                    const fullId = getFullStationId(station);
                    const party = [station.countryCode, station.partyId].filter(Boolean).join(':');
                    return (
                      <div key={fullId} className="rfid-selected-user-card">
                        <div className="rfid-selected-user-card__header">
                          <span className="rfid-selected-user-card__name">
                            {station.name || fullId}
                          </span>
                          <div className="rfid-selected-user-card__header-right">
                            <span className="rfid-selected-user-card__id">{fullId}</span>
                            <button
                              type="button"
                              className="rfid-selected-user-card__remove-btn"
                              onClick={() =>
                                setSelectedStationIds((previous) =>
                                  previous.filter((id) => id !== fullId),
                                )
                              }
                              aria-label={`Remove ${station.name || fullId}`}
                              title={t('remove', { defaultValue: 'Remove' })}>
                              ✕
                            </button>
                          </div>
                        </div>
                        {party && (
                          <div className="rfid-selected-user-card__details">
                            <div className="rfid-selected-user-card__detail-item">
                              <span className="rfid-selected-user-card__detail-label">
                                {t('operator', { defaultValue: 'Party / Operator' })}:
                              </span>
                              <span className="rfid-selected-user-card__detail-value">{party}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {cardType === 'BUSINESS_TAP' && businessType === 'GROUP' && (
          <div className="rfid-modal-form__field">
            <SelectField
              inputLabel={t('cardGroup', { defaultValue: 'Card Group' })}
              name="selectedGroupId"
              options={groupOptions}
              selectedValue={selectedGroupId}
              valueRequired
              error={Boolean(errors.selectedGroupId)}
              errorMessage={errors.selectedGroupId}
              onChangeValue={(_, value) => {
                setSelectedGroupId(value);
                if (errors.selectedGroupId) {
                  setErrors((previous) => ({ ...previous, selectedGroupId: undefined }));
                }
              }}
              loading={groupsLoading}
              placeholder={
                groupsLoading
                  ? t('loadingCardGroups', { defaultValue: 'Loading card groups...' })
                  : t('selectCardGroup', { defaultValue: 'Select card group' })
              }
            />
            {errors.selectedGroupId && (
              <span className="text-danger">{errors.selectedGroupId}</span>
            )}

            {selectedGroup && (
              <div className="rfid-selected-user-card">
                <div className="rfid-selected-user-card__header">
                  <span className="rfid-selected-user-card__name">
                    {selectedGroup.name || 'Card Group'}
                  </span>
                  <span className="rfid-selected-user-card__id">
                    {selectedGroup._id || selectedGroup.id}
                  </span>
                </div>
                <div className="rfid-selected-user-card__details">
                  <div className="rfid-selected-user-card__detail-item">
                    <span className="rfid-selected-user-card__detail-label">
                      {t('availableBalance', { defaultValue: 'Available Balance' })}:
                    </span>
                    <span className="rfid-selected-user-card__detail-value">
                      {getBalanceKwh(selectedGroup.balanceKwh)} kWh
                    </span>
                  </div>
                  {Array.isArray(selectedGroup.stationIds) &&
                    selectedGroup.stationIds.length > 0 && (
                      <div className="rfid-selected-user-card__detail-item">
                        <span className="rfid-selected-user-card__detail-label">
                          {t('stations', { defaultValue: 'Stations' })}:
                        </span>
                        <span className="rfid-selected-user-card__detail-value">
                          {selectedGroup.stationIds.length}{' '}
                          {t('authorized', { defaultValue: 'authorized' })}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        )}

        {(cardType === 'PERSONAL_TAP' || (cardType === 'BUSINESS_TAP' && businessType === 'INDIVIDUAL')) && (
          <div className="rfid-modal-form__field">
            <SelectField
              inputLabel={t('customerAccountOptional', {
                defaultValue: 'Customer Account (Optional)',
              })}
              name="customerId"
              options={customerOptions}
              selectedValue={customerId}
              error={Boolean(errors.customerId)}
              errorMessage={errors.customerId}
              onChangeValue={(_, value) => {
                setCustomerId(value);
                if (errors.customerId) {
                  setErrors((previous) => ({ ...previous, customerId: undefined }));
                }
              }}
              loading={customersLoading}
              placeholder={
                customersLoading
                  ? t('loadingCustomers', { defaultValue: 'Loading customer accounts...' })
                  : t('selectCustomerAccount', { defaultValue: 'Select customer account' })
              }
            />
            {errors.customerId && <span className="text-danger">{errors.customerId}</span>}

            {selectedCustomer && (
              <div className="rfid-selected-user-card">
                <div className="rfid-selected-user-card__header">
                  <span className="rfid-selected-user-card__name">
                    {selectedCustomer.name || selectedCustomer.userCustomId}
                  </span>
                  <div className="rfid-selected-user-card__header-right">
                    <span className="rfid-selected-user-card__id">
                      {selectedCustomer.userCustomId}
                    </span>
                    <button
                      type="button"
                      className="rfid-selected-user-card__remove-btn"
                      onClick={() => setCustomerId('')}
                      aria-label={`Remove ${selectedCustomer.name || selectedCustomer.userCustomId}`}
                      title={t('remove', { defaultValue: 'Remove' })}>
                      ✕
                    </button>
                  </div>
                </div>
                <div className="rfid-selected-user-card__details">
                  {selectedCustomer.email && (
                    <div className="rfid-selected-user-card__detail-item">
                      <span className="rfid-selected-user-card__detail-label">
                        {t('email', { defaultValue: 'Email' })}:
                      </span>
                      <span className="rfid-selected-user-card__detail-value">
                        {selectedCustomer.email}
                      </span>
                    </div>
                  )}
                  {selectedCustomer.phoneNumber && (
                    <div className="rfid-selected-user-card__detail-item">
                      <span className="rfid-selected-user-card__detail-label">
                        {t('phone', { defaultValue: 'Phone' })}:
                      </span>
                      <span className="rfid-selected-user-card__detail-value">
                        {selectedCustomer.phoneNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rfid-modal-form__actions">
        <MyButton
          label={t('cancel', { defaultValue: 'Cancel' })}
          buttonType="outline"
          onClickFunc={handleCancel}
        />
        <MyButton
          label={
            actionLoading
              ? t('registeringCard', { defaultValue: 'Registering...' })
              : t('registerCard', { defaultValue: 'Register Card' })
          }
          buttonType="primary"
          onClickFunc={handleSubmit}
          disableButton={actionLoading}
        />
      </div>
    </div>
  );
}

export default AddCardModal;
